import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Grid, 
  TextField, 
  Alert, 
  Snackbar, 
  IconButton,
  Chip,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  People as PeopleIcon,
  LocalHospital as DoctorIcon,
  Visibility as ReceptionIcon,
  MedicalServices as OptometristIcon,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import StatCard from '../components/StatCard';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'reception', phone: '' });
  const [stats, setStats] = useState({
    total: 0,
    doctors: 0,
    reception: 0,
    optometrists: 0
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getUsers();
      const usersData = response.data.data;
      setUsers(usersData);
      
      // Calculate stats
      const total = usersData.length;
      const doctors = usersData.filter(u => u.role === 'doctor').length;
      const reception = usersData.filter(u => u.role === 'reception').length;
      const optometrists = usersData.filter(u => u.role === 'optometrist').length;
      
      setStats({ total, doctors, reception, optometrists });
    } catch (error) {
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        const updateData = { ...userForm };
        if (!updateData.password) delete updateData.password;
        await authAPI.updateUser(editingUser._id, updateData);
        showSnackbar('User updated!');
      } else {
        await authAPI.createUser(userForm);
        showSnackbar('User created!');
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Failed', 'error');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'error',
      doctor: 'primary',
      optometrist: 'success',
      reception: 'info',
      opd: 'warning'
    };
    return colors[role] || 'default';
  };

  const columns = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'email', headerName: 'Email', width: 250 },
    { 
      field: 'role', 
      headerName: 'Role', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value.toUpperCase()} 
          color={getRoleBadgeColor(params.value)}
          size="small"
          sx={{ fontWeight: 'bold' }}
        />
      )
    },
    { field: 'phone', headerName: 'Phone', width: 150 },
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight="700">
          Users
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <StatCard
          label="Total Users"
          value={stats.total}
          icon={<PeopleIcon fontSize="small" />}
          color="primary"
          subtitle="All staff members"
        />
        <StatCard
          label="Doctors"
          value={stats.doctors}
          icon={<DoctorIcon fontSize="small" />}
          color="secondary"
          subtitle={`${stats.total > 0 ? Math.round((stats.doctors/stats.total)*100) : 0}% of staff`}
        />
        <StatCard
          label="Reception"
          value={stats.reception}
          icon={<ReceptionIcon fontSize="small" />}
          color="info"
          subtitle={`${stats.total > 0 ? Math.round((stats.reception/stats.total)*100) : 0}% of staff`}
        />
        <StatCard
          label="Optometrists"
          value={stats.optometrists}
          icon={<OptometristIcon fontSize="small" />}
          color="success"
          subtitle={`${stats.total > 0 ? Math.round((stats.optometrists/stats.total)*100) : 0}% of staff`}
        />
      </Box>

      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add User
        </Button>
      </Box>

      {/* Data Grid */}
      <Paper variant="outlined" sx={{ height: 600, width: '100%', borderRadius: 2, boxShadow: 'none' }}>
        <DataGrid 
          rows={users} 
          columns={columns} 
          pageSize={10} 
          loading={loading} 
          getRowId={(row) => row._id}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(240, 147, 251, 0.04)',
            },
          }}
        />
      </Paper>
      {/* Add/Edit User Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                required
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                required
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                required={!editingUser}
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                helperText={editingUser ? 'Leave blank to keep current password' : ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Role"
                required
                value={userForm.role}
                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="doctor">Doctor</MenuItem>
                <MenuItem value="optometrist">Optometrist</MenuItem>
                <MenuItem value="reception">Reception</MenuItem>
                <MenuItem value="opd">OPD</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={userForm.phone}
                onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
          >
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
